import { useState } from "react";
import type { KnowledgeArticle } from "@/types/knowledgeBase";
import { knowledgeCategories } from "@/types/knowledgeBase";

interface ArticleFormModalProps {
  isOpen: boolean;
  article: KnowledgeArticle | null;
  onClose: () => void;
  onSubmit: (data: Omit<KnowledgeArticle, "id" | "article_code"> & { id?: string; article_code?: string }) => void;
  isLoading: boolean;
}

export default function ArticleFormModal({
  isOpen,
  article,
  onClose,
  onSubmit,
  isLoading,
}: ArticleFormModalProps) {
  const isEdit = !!article;

  const [form, setForm] = useState({
    article_code: article?.article_code || "",
    category: article?.category || "product",
    subCategory: article?.subCategory || "",
    tags: article?.tags?.join(", ") || "",
    title: article?.title || "",
    description: article?.description || "",
    suggestedReply: article?.suggestedReply || "",
    sop: article?.sop?.join("\n") || "",
    departments: article?.departments?.join(", ") || "",
    relatedIds: article?.relatedIds?.join(", ") || "",
  });

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const tags = form.tags
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const sop = form.sop
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    const departments = form.departments
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const relatedIds = form.relatedIds
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    onSubmit({
      id: isEdit ? article?.id : undefined,
      article_code: form.article_code,
      category: form.category,
      subCategory: form.subCategory,
      tags,
      title: form.title,
      description: form.description,
      suggestedReply: form.suggestedReply,
      sop,
      departments,
      relatedIds,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.4)" }}
    >
      <div className="bg-background-50 rounded-xl border border-background-200 w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-background-100 flex items-center justify-between shrink-0">
          <h3 className="text-base font-bold text-foreground-900">
            {isEdit ? "編輯知識文章" : "新增知識文章"}
          </h3>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-full bg-background-100 hover:bg-background-200 flex items-center justify-center text-foreground-500 cursor-pointer transition-colors"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-foreground-700 block mb-1.5">
                文章編號 <span className="text-accent-500">*</span>
              </label>
              <input
                type="text"
                value={form.article_code}
                onChange={(e) => handleChange("article_code", e.target.value)}
                placeholder="例如 L-014"
                required
                className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-foreground-700 block mb-1.5">
                主分類 <span className="text-accent-500">*</span>
              </label>
              <select
                value={form.category}
                onChange={(e) => handleChange("category", e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 bg-background-50"
              >
                {knowledgeCategories
                  .filter((c) => c.id !== "all")
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-foreground-700 block mb-1.5">
              子分類 <span className="text-accent-500">*</span>
            </label>
            <input
              type="text"
              value={form.subCategory}
              onChange={(e) => handleChange("subCategory", e.target.value)}
              placeholder="例如 系統問題、退貨退款..."
              required
              className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-foreground-700 block mb-1.5">
              標題 <span className="text-accent-500">*</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => handleChange("title", e.target.value)}
              placeholder="客戶反映...？"
              required
              className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-foreground-700 block mb-1.5">
              標籤（逗號分隔）
            </label>
            <input
              type="text"
              value={form.tags}
              onChange={(e) => handleChange("tags", e.target.value)}
              placeholder="系統, Bug, 異常"
              className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-foreground-700 block mb-1.5">
              問題描述 <span className="text-accent-500">*</span>
            </label>
            <textarea
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="對應部門與建議話術摘要..."
              required
              rows={3}
              maxLength={500}
              className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 resize-none"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-foreground-700 block mb-1.5">
              建議回覆話術 <span className="text-accent-500">*</span>
            </label>
            <textarea
              value={form.suggestedReply}
              onChange={(e) => handleChange("suggestedReply", e.target.value)}
              placeholder="完整的建議回覆話術..."
              required
              rows={4}
              maxLength={1000}
              className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 resize-none"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-foreground-700 block mb-1.5">
              員工 SOP（每行一個步驟）
            </label>
            <textarea
              value={form.sop}
              onChange={(e) => handleChange("sop", e.target.value)}
              placeholder="步驟一\n步驟二\n步驟三"
              rows={4}
              maxLength={1000}
              className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-foreground-700 block mb-1.5">
                對應部門（逗號分隔）
              </label>
              <input
                type="text"
                value={form.departments}
                onChange={(e) => handleChange("departments", e.target.value)}
                placeholder="物流部, 會計部"
                className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-foreground-700 block mb-1.5">
                關聯文章編號（逗號分隔）
              </label>
              <input
                type="text"
                value={form.relatedIds}
                onChange={(e) => handleChange("relatedIds", e.target.value)}
                placeholder="L-001, L-002"
                className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
              />
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-background-100 flex items-center justify-end gap-2 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-background-200 text-sm font-medium text-foreground-600 hover:bg-background-100 cursor-pointer transition-colors whitespace-nowrap"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading && <i className="ri-loader-4-line animate-spin" />}
            {isEdit ? "儲存修改" : "新增文章"}
          </button>
        </div>
      </div>
    </div>
  );
}